import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Download, Search, RotateCcw } from "lucide-react";
import { toast } from "sonner";

// Convert stored UTC ISO timestamp to IST wall-clock display.
const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    const s = iso.endsWith("Z") || iso.match(/[+-]\d{2}:\d{2}$/) ? iso : iso + "Z";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 19).replace("T", " ");
    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    });
  } catch {
    return String(iso).slice(0, 19).replace("T", " ");
  }
};

export default function NotificationHistory() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ client_id: "", action: "", alert_type: "", email_status: "", from_date: "", to_date: "", search: "" });
  const [testEmailTo, setTestEmailTo] = useState("");
  const [testEmailBusy, setTestEmailBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [rowsR, sumR] = await Promise.all([
        api.get("/notifications", { params }),
        api.get("/notifications/summary", { params: { from_date: filters.from_date || undefined, to_date: filters.to_date || undefined } }),
      ]);
      setRows(rowsR.data.rows || []);
      setSummary(sumR.data || null);
    } catch {
      toast.error("Failed to load notifications");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const downloadCsv = async () => {
    const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v)));
    const tok = localStorage.getItem("pagewise_token");
    const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications.csv?${params}`,
      { headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = window.URL.createObjectURL(blob);
    a.download = "notifications.csv";
    a.click();
  };

  const totals = useMemo(() => rows.reduce((a, r) => ({
    total: a.total + 1,
    email_sent: a.email_sent + (r.email_status === "sent" ? 1 : 0),
    tg_sent: a.tg_sent + (r.telegram_status === "sent" ? 1 : 0),
    failed: a.failed + ((r.email_status === "failed" || r.telegram_status === "failed") ? 1 : 0),
  }), { total: 0, email_sent: 0, tg_sent: 0, failed: 0 }), [rows]);

  const runTestEmail = async () => {
    if (!testEmailTo) { toast.error("Enter a recipient email"); return; }
    setTestEmailBusy(true);
    setTestResult(null);
    try {
      const r = await api.post("/admin/notifications/test-email", { to: testEmailTo });
      setTestResult(r.data);
      if (r.data.ok) toast.success("Test email sent — check inbox (and Spam folder)");
      else toast.error(`Test failed: ${r.data.reason || "see result panel"}`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data?.reason || e.message;
      setTestResult({ ok: false, reason: detail });
      toast.error(`Test failed: ${detail}`);
    } finally {
      setTestEmailBusy(false);
    }
  };

  const StatusBadge = ({ s, reason }) => {
    const title = reason ? String(reason) : undefined;
    if (s === "sent") return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">✓ sent</Badge>;
    if (s === "failed") return (
      <Badge
        title={title}
        className="bg-rose-50 text-rose-700 border-rose-200 cursor-help"
        data-testid="failed-badge"
      >
        ✗ failed
      </Badge>
    );
    if (s === "skipped") return (
      <Badge title={title} className="bg-amber-50 text-amber-700 border-amber-200 cursor-help">
        ⚠ skipped
      </Badge>
    );
    return <span className="text-zinc-400 text-xs">—</span>;
  };

  const AlertTypeBadge = ({ type }) => {
    const map = {
      recommendation: { cls: "bg-blue-50 text-blue-700 border-blue-200", label: "RECOMMENDATION" },
      activation: { cls: "bg-orange-50 text-orange-700 border-orange-200", label: "ACTIVATION" },
      portfolio_update: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "PORTFOLIO" },
      system: { cls: "bg-zinc-100 text-zinc-700 border-zinc-200", label: "SYSTEM" },
    };
    const conf = map[type] || map.recommendation;
    return <Badge className={conf.cls}>{conf.label}</Badge>;
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="notification-history-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-display font-semibold text-zinc-950">Notification History</h1>
          <p className="text-sm text-zinc-500 mt-1">Every alert dispatched to a client — email + Telegram status, with filters &amp; export.</p>
        </div>
        <Button variant="outline" onClick={downloadCsv} data-testid="notif-history-export-csv">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-xs text-zinc-500 font-medium">From</label>
            <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} data-testid="notif-filter-from" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 font-medium">To</label>
            <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} data-testid="notif-filter-to" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 font-medium">Type</label>
            <select value={filters.alert_type} onChange={(e) => setFilters({ ...filters, alert_type: e.target.value })} className="mt-1 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="notif-filter-alert-type">
              <option value="">All Types</option>
              <option value="recommendation">Recommendation</option>
              <option value="activation">Activation</option>
              <option value="portfolio_update">Portfolio Update</option>
              <option value="system">System</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500 font-medium">Action</label>
            <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} className="mt-1 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="notif-filter-action">
              <option value="">All</option><option>BUY</option><option>SELL</option><option>HOLD</option>
            </select>
          </div>
          <div className="md:col-span-2 flex gap-2">
            <Input placeholder="Search client / stock / symbol / notes…" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="notif-filter-search" />
            <Button onClick={load} data-testid="notif-filter-apply"><Search className="w-4 h-4" /></Button>
            <Button variant="ghost" onClick={() => { setFilters({ client_id: "", action: "", from_date: "", to_date: "", search: "" }); setTimeout(load, 50); }} data-testid="notif-filter-reset"><RotateCcw className="w-4 h-4" /></Button>
          </div>
        </div>
      </Card>

      <Card className="p-4 border-blue-200 bg-blue-50/30" data-testid="test-email-card">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-sm font-display font-semibold text-zinc-900">SMTP Diagnostic</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              Sends a minimal test email through Zoho SMTP so you can verify deliverability without triggering a real alert.
            </div>
          </div>
          <div className="flex gap-2 items-end">
            <div>
              <label className="text-xs text-zinc-500 font-medium">Recipient</label>
              <Input
                type="email"
                placeholder="you@example.com"
                value={testEmailTo}
                onChange={(e) => setTestEmailTo(e.target.value)}
                className="w-64"
                data-testid="test-email-to"
              />
            </div>
            <Button onClick={runTestEmail} disabled={testEmailBusy || !testEmailTo} data-testid="test-email-btn">
              {testEmailBusy ? "Sending…" : "Send Test Email"}
            </Button>
          </div>
        </div>
        {testResult && (
          <div className={`mt-3 p-3 rounded-md text-xs font-mono ${testResult.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-rose-50 border border-rose-200 text-rose-900"}`} data-testid="test-email-result">
            <div className="font-bold mb-1">{testResult.ok ? "✓ Delivered to Zoho" : "✗ Failed"}</div>
            {testResult.smtp_response && <div>SMTP Response: {testResult.smtp_response}</div>}
            {testResult.reason && <div>Reason: {testResult.reason}</div>}
            {testResult.exception_type && <div>Exception: {testResult.exception_type}</div>}
            {testResult.config && (
              <div className="mt-1 text-[10px] text-zinc-600">
                Config: {testResult.config.host}:{testResult.config.port} as {testResult.config.user}
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Total" value={summary?.total ?? totals.total} />
        <Tile label="Email sent" value={summary?.sent ?? totals.email_sent} />
        <Tile label="Failed" value={summary?.failed ?? totals.failed} />
        <Tile label="Skipped" value={summary?.skipped ?? 0} />
      </div>

      {summary?.by_type && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="notif-type-tiles">
          <Tile label="Recommendations" value={summary.by_type.recommendation || 0} />
          <Tile label="Activation reminders" value={summary.by_type.activation || 0} />
          <Tile label="Portfolio updates" value={summary.by_type.portfolio_update || 0} />
          <Tile label="System" value={summary.by_type.system || 0} />
        </div>
      )}

      {totals.failed > 0 && (
        <div className="flex items-center justify-between gap-3 p-3 rounded-md border border-rose-200 bg-rose-50">
          <div className="text-sm text-rose-800">
            <strong>{totals.failed}</strong> failed emails in current view.
            Fix your SMTP block (see SMTP Diagnostic above), then click Resend All Failed.
          </div>
          <Button
            size="sm"
            variant="outline"
            className="bg-white border-rose-300 text-rose-700 hover:bg-rose-100"
            data-testid="resend-all-failed-btn"
            onClick={async () => {
              const failedRows = rows.filter((r) => r.email_status === "failed");
              if (!failedRows.length) { toast.info("Nothing to resend"); return; }
              if (!window.confirm(`Resend ${failedRows.length} failed emails? This may take a minute.`)) return;
              let ok = 0, ko = 0;
              for (const r of failedRows) {
                try {
                  const res = await api.post(`/notifications/${r.notif_id}/resend`);
                  if (res.data.email?.status === "sent") ok++; else ko++;
                } catch { ko++; }
              }
              toast.success(`Resend complete: ${ok} sent, ${ko} failed`);
              await load();
            }}
          >
            Resend All Failed
          </Button>
        </div>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm table-auto">
            <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-3 py-3 text-left whitespace-nowrap">Sent</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Type</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Client</th>
                <th className="px-3 py-3 text-left">Action</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Stock</th>
                <th className="px-3 py-3 text-right">Qty</th>
                <th className="px-3 py-3 text-right whitespace-nowrap">Price</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Date</th>
                <th className="px-3 py-3 text-left">Email</th>
                <th className="px-3 py-3 text-left">Telegram</th>
                <th className="px-3 py-3 text-left">Notes</th>
                <th className="px-3 py-3 text-right whitespace-nowrap sticky right-0 bg-zinc-50 border-l border-zinc-200">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={12} className="px-4 py-8 text-center text-zinc-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={12} className="px-4 py-8 text-center text-zinc-400">No notifications yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.notif_id} className="border-t border-zinc-100 hover:bg-zinc-50 group" data-testid={`notif-row-${r.notif_id}`}>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{fmtIST(r.sent_at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <AlertTypeBadge type={r.alert_type || "recommendation"} />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.client_name}</td>
                  <td className="px-3 py-2">
                    <Badge className={r.action === "BUY" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : r.action === "SELL" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}>{r.action}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    <div>{r.stock_name}</div>
                    <div className="text-[10px] font-mono text-zinc-400">{r.symbol}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{r.qty != null ? (Math.abs(r.qty - Math.round(r.qty)) < 1e-6 ? r.qty.toFixed(0) : r.qty.toFixed(4)) : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">{r.price != null ? `₹${r.price.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.date}</td>
                  <td className="px-3 py-2"><StatusBadge s={r.email_status} reason={r.email_reason} /></td>
                  <td className="px-3 py-2"><StatusBadge s={r.telegram_status} reason={r.telegram_reason} /></td>
                  <td className="px-3 py-2 text-xs text-zinc-600 max-w-[260px]">
                    <div className="line-clamp-2" title={r.notes}>{r.notes}</div>
                    {(r.email_reason || r.telegram_reason) && (
                      <div className="text-[10px] text-rose-600 mt-1 font-mono break-all line-clamp-2" data-testid={`notif-reason-${r.notif_id}`}>
                        {r.email_reason && <div className="line-clamp-2">Email: {r.email_reason}</div>}
                        {r.telegram_reason && <div className="line-clamp-1">Telegram: {r.telegram_reason}</div>}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap sticky right-0 bg-white group-hover:bg-zinc-50 border-l border-zinc-200">
                    {r.email_status === "failed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            const res = await api.post(`/notifications/${r.notif_id}/resend`);
                            const es = res.data.email?.status;
                            if (es === "sent") toast.success("Resent — email delivered");
                            else toast.error(`Resend failed: ${res.data.email?.reason || "unknown"}`);
                            await load();
                          } catch (e) {
                            toast.error(`Resend error: ${e.response?.data?.detail || e.message}`);
                          }
                        }}
                        className="text-xs h-7 bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100"
                        data-testid={`resend-btn-${r.notif_id}`}
                      >
                        Resend
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-display font-semibold text-zinc-950 mt-1">{value}</div>
    </Card>
  );
}
